import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowRight, Briefcase, PartyPopper, Check } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { translations } from "@/i18n/translations";

const scrollTo = (id: string) => {
  const element = document.getElementById(id);
  if (element) element.scrollIntoView({ behavior: "smooth" });
};

const AudienceSplit = () => {
  const { language } = useLanguage();
  const t = translations[language].audience;

  const options = [
    { ...t.business, icon: Briefcase, target: "teambuilding" },
    { ...t.party, icon: PartyPopper, target: "experiences" },
  ];

  return (
    <section id="audience" className="py-14 sm:py-24 scroll-mt-20 relative">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8 sm:mb-16">
          <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-4">{t.title}</h2>
          <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto">
            {t.subtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 max-w-5xl mx-auto">
          {options.map((option) => {
            const Icon = option.icon;
            return (
              <Card
                key={option.target}
                onClick={() => scrollTo(option.target)}
                className="group cursor-pointer bg-transparent border-border hover:border-primary/60 transition-colors duration-300 p-8 sm:p-10 flex flex-col"
              >
                <div className="flex items-center gap-3 mb-4">
                  <Icon className="w-6 h-6 text-primary shrink-0" />
                  <Badge variant="outline" className="bg-transparent text-muted-foreground border-border font-normal">{option.badge}</Badge>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold mb-3 group-hover:text-primary transition-colors">
                  {option.title}
                </h3>
                <p className="text-sm sm:text-base text-muted-foreground mb-5">
                  {option.description}
                </p>
                <ul className="space-y-2 mb-6">
                  {option.bullets.map((bullet, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm sm:text-base">
                      <Check className="w-4 h-4 text-primary mt-1 shrink-0" />
                      <span>{bullet}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-auto">
                  <Button
                    variant="ghost"
                    className="px-0 text-primary hover:bg-transparent hover:text-primary font-semibold"
                  >
                    {option.cta}
                    <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default AudienceSplit;
